<script lang="ts">
  import { onMount } from 'svelte';
  import { get } from 'svelte/store';
  import { page, router } from '@inertiajs/svelte';
  import type {
    DashboardProps,
    Scale,
    View,
    MonthRange,
    WeekPeriod,
    TablePeriod,
    DisplayFilter,
    Range,
    FavoriteView,
  } from '../lib/types';
  import { getValidRangesForView, getDefaultRangeForView } from '../lib/types';
  import {
    isWorkQueueOpen,
    draggedProject,
    requestProjectMove,
    isShortcutsMinimized,
    toggleShortcutsBar,
    isStatusRailOpen,
    isMonthRailOpen,
    isEventsRailOpen,
    closeAllRightRails,
  } from '../lib/stores/ui';
  import { favoriteViews } from '../lib/stores/favorites';
  import { navigateToFavorite } from '../lib/favoritesNavigation';
  import { isDialogOpen, shouldHandleTodayHotkey } from '../lib/pageHotkeys';
  import DayCellPopover from '../components/DayCellPopover.svelte';
  import ProjectTooltip from '../components/ProjectTooltip.svelte';
  import ProjectCellPopover from '../components/ProjectCellPopover.svelte';
  import HoveredDateLabel from '../components/HoveredDateLabel.svelte';
  import TimelineTopBar from '../components/mobile/TimelineTopBar.svelte';
  import MobileDaysAgenda from '../components/mobile/MobileDaysAgenda.svelte';
  import DesktopOnlyNotice from '../components/DesktopOnlyNotice.svelte';
  import { MOBILE_TIMELINE_VIEWS, isMobileTimelineView } from '../lib/mobileTimeline';
  import { timelineUrlForView } from '../lib/timelineUrl';
  import { getLaunchRedirectPath } from '../lib/timelinePath';
  import { viewport } from '../lib/stores/viewport';
  import {
    getCurrentMonthKey,
    getPreviousMonthKey,
    getNextMonthKey,
    getCurrentWeekKey,
    getPreviousTimeframe,
    getNextTimeframe,
    getPreviousMonthTimeframe,
    getNextMonthTimeframe,
    getEffectiveRangeParam,
  } from '../lib/utils';
  import {
    Header,
    BacklogRail,
    MobileSheet,
    StatusRail,
    MonthRail,
    EventsRail,
    EventModal,
    ProjectModal,
    ProjectRail,
    PriorityTasksRail,
    MoveConfirmModal,
    EventMoveConfirmModal,
    YearGrid6x2View,
    YearGridQuartersView,
    YearTableView,
    WeekTableView,
    WeekTableDaysView,
    WeekFocusView,
    WeekRollingView,
    WeekTimelineView,
    GanttChartView,
    MonthRollingView,
    MonthTimelineView,
    MonthFocusView,
    TaskKanbanView,
  } from '../components';

  // Props from Rails DashboardController
  let {
    backlogProjects,
    scheduledProjects,
    projectsByMonth,
    projectsByWeek = {},
    events,
    currentMonth,
    currentWeek,
    currentYear,
    visibleMonths,
    visibleWeeks = [],
    scale,
    view,
    range,
    display,
    viewConfig,
  }: DashboardProps = $props();

  // Favorites for keyboard shortcuts
  let currentFavorites = $derived($favoriteViews);

  // Flatten all projects for ProjectRail lookup
  let allProjectsFlat = $derived.by(() => {
    const seen = new Set<string>();
    const result: import('../lib/types').Project[] = [];
    const addProject = (p: import('../lib/types').Project) => {
      if (!seen.has(p.id)) {
        seen.add(p.id);
        result.push(p);
      }
    };
    backlogProjects.forEach(addProject);
    // scheduledProjects is global (not limited to the visible timeline window) and feeds
    // the Days view panel. Include it so ProjectRail can resolve every project the panel
    // can show — otherwise clicking a card for a project outside the visible window (or
    // with status "almost", which month/week grouping drops) silently does nothing.
    scheduledProjects.forEach(addProject);
    for (const statuses of Object.values(projectsByMonth)) {
      for (const projects of Object.values(statuses)) {
        (projects as import('../lib/types').Project[]).forEach(addProject);
      }
    }
    for (const statuses of Object.values(projectsByWeek)) {
      for (const projects of Object.values(statuses)) {
        (projects as import('../lib/types').Project[]).forEach(addProject);
      }
    }
    return result;
  });

  // Mobile: the rails below share one bottom sheet (ProjectRail renders its own).
  let isRailSheetOpen = $derived(
    $isStatusRailOpen || $isMonthRailOpen || $isEventsRailOpen || $isWorkQueueOpen,
  );
  let railSheetLabel = $derived(
    $isWorkQueueOpen
      ? 'Work queue'
      : $isEventsRailOpen
        ? 'Events'
        : $isMonthRailOpen
          ? 'Projects for this period'
          : 'Projects by status',
  );

  // View helpers
  let isYearScale = $derived(scale === 'year');
  let isWeekScale = $derived(scale === 'week');
  let isMonthScale = $derived(scale === 'month');

  // Map new view names to components
  function getViewComponent(): string {
    if (scale === 'year') {
      switch (view) {
        case 'focus':
          return 'year-focus';
        case 'tasks':
          return 'year-tasks';
        case 'grid-6x2':
          return 'year-grid-6x2';
        case 'grid-quarters':
          return 'year-grid-quarters';
        case 'table':
          return 'year-table';
        default:
          return 'year-grid-6x2';
      }
    }
    if (scale === 'week') {
      switch (view) {
        case 'focus':
          return 'week-focus';
        case 'rolling':
          return 'week-rolling';
        case 'timeline':
          return 'week-timeline';
        case 'table':
          return 'week-table';
        case 'days':
          return 'week-table-days';
        case 'gantt':
          return 'week-gantt';
        case 'tasks':
          return 'week-tasks';
        default:
          return 'week-table';
      }
    }
    // Month scale
    switch (view) {
      case 'focus':
        return 'month-focus';
      case 'rolling':
        return 'month-rolling';
      case 'timeline':
        return 'month-timeline';
      case 'days':
        return 'month-table-days';
      case 'gantt':
        return 'month-gantt';
      case 'tasks':
        return 'month-tasks';
      default:
        return 'month-focus';
    }
  }

  let viewComponent = $derived(getViewComponent());

  // Phones only get Days, Focus and Tasks. A deep link to any other view shows a
  // notice in its place (plans/mobile-v2.md §3.6, D8: no auto-redirect).
  let showDesktopOnlyNotice = $derived($viewport.isMobile && !isMobileTimelineView(view));
  function goToMobileView(target: View) {
    const url = timelineUrlForView(
      { scale, view, range, display, currentMonth, currentWeek, currentYear, viewConfig },
      target,
    );
    if (url) router.get(url, {}, { preserveScroll: true });
  }
  let mobileViewActions = $derived(
    MOBILE_TIMELINE_VIEWS.map((v) => ({
      label:
        `${viewConfig.viewLabels[v]?.icon ?? ''} ${viewConfig.viewLabels[v]?.label ?? v}`.trim(),
      onSelect: () => goToMobileView(v),
    })),
  );

  // Phones show Days as a week strip + agenda (ma-xemu). The agenda has its own ‹ ›,
  // and is remounted per period so it opens on the right week.
  let showMobileDays = $derived($viewport.isMobile && view === 'days');
  let mobileDaysAgenda = $state<ReturnType<typeof MobileDaysAgenda> | undefined>();
  function goToTodayFromHeader() {
    // On the current period already: bring today's week into view instead.
    if (!goToToday() && showMobileDays) mobileDaysAgenda?.scrollToToday();
  }

  // Launching the installed app opens /dashboard, which redirects to the #1
  // favorite (flash `launch`). On a phone, a desktop-only #1 favorite is swapped
  // for the page the Timeline tab opens (ma-nvyu). Inertia remounts the page on
  // every non-preserveState visit, so a mount check sees each launch. Favorites
  // come from the page props: Layout's store init may run after this.
  onMount(() => {
    const { flash, props } = get(page);
    const favorites = (props.preferences as { favoriteViews?: FavoriteView[] } | undefined)
      ?.favoriteViews;
    const path = getLaunchRedirectPath({
      launchedFromDefault: flash?.launch === 'default',
      isMobile: get(viewport).isMobile,
      view,
      favorites: favorites ?? [],
    });
    if (path) router.visit(path, { replace: true });
  });

  // Cycle helpers
  function cycleScale() {
    const allScales: Scale[] = ['week', 'month', 'year'];
    // Only cycle through scales that support the current view
    const validScales = allScales.filter((s) => (viewConfig.viewsByScale[s] || []).includes(view));
    if (validScales.length <= 1) return;

    const currentIdx = validScales.indexOf(scale);
    const nextScale = validScales[(currentIdx + 1) % validScales.length];
    const effectiveScale = view === 'days' ? 'week' : nextScale;
    const newRange = (getDefaultRangeForView(view, effectiveScale, viewConfig) ??
      viewConfig.defaultRanges[nextScale]) as Range;

    let timeKey: string;
    switch (nextScale) {
      case 'year':
        timeKey = String(new Date().getFullYear());
        break;
      case 'week':
        timeKey = getCurrentWeekKey();
        break;
      default:
        timeKey = getCurrentMonthKey();
        break;
    }

    router.get(buildUrl(nextScale, timeKey, view, newRange), {}, { preserveScroll: true });
  }

  function cycleRange() {
    // Only views with range support should cycle
    if (!viewConfig.viewsWithRangeSupport.includes(view)) return;
    // Use view-specific ranges if defined, otherwise fall back to scale ranges
    const effectiveScale = view === 'days' ? 'week' : scale;
    const viewSpecificRanges = viewConfig.viewSpecificRanges?.[view]?.[effectiveScale];
    const validRanges =
      viewSpecificRanges && viewSpecificRanges.length > 0
        ? viewSpecificRanges
        : viewConfig.validRanges[effectiveScale] || [];
    if (validRanges.length <= 1) return;
    const currentIdx = validRanges.indexOf(range as string);
    const nextRange = validRanges[(currentIdx + 1) % validRanges.length];
    router.get(
      buildUrl(scale, getTimeKey(), view, nextRange as Range),
      {},
      { preserveScroll: true },
    );
  }

  function cycleView() {
    // Cycle all views in UI display order: primary views then secondary views
    const allViews: View[] = [...viewConfig.primaryViews, ...viewConfig.secondaryViews];
    if (allViews.length <= 1) return;
    const currentIdx = allViews.indexOf(view);
    const nextView = allViews[(currentIdx + 1) % allViews.length];

    // If next view isn't valid for current scale, find a valid scale
    let targetScale = scale;
    if (!viewConfig.viewsByScale[targetScale]?.includes(nextView)) {
      for (const s of ['week', 'month', 'year'] as Scale[]) {
        if (viewConfig.viewsByScale[s]?.includes(nextView)) {
          targetScale = s;
          break;
        }
      }
    }

    // Use the appropriate time key for the target scale
    let timeKey: string;
    switch (targetScale) {
      case 'year':
        timeKey = String(currentYear);
        break;
      case 'week':
        timeKey = currentWeek || getCurrentWeekKey();
        break;
      default:
        timeKey = currentMonth || getCurrentMonthKey();
        break;
    }

    const effectiveScaleForRange = nextView === 'days' ? 'week' : targetScale;
    const targetRange = (getDefaultRangeForView(nextView, effectiveScaleForRange, viewConfig) ??
      viewConfig.defaultRanges[targetScale]) as Range;
    const targetDisplay = (viewConfig.defaultDisplay[nextView] as DisplayFilter) || null;
    router.get(
      buildUrlWithDisplay(targetScale, timeKey, nextView, targetRange, targetDisplay),
      {},
      { preserveScroll: true },
    );
  }

  function cycleDisplay() {
    const validOptions = (viewConfig.validDisplayOptions[view] as DisplayFilter[]) || [];
    if (validOptions.length <= 1) return;
    const currentIdx = validOptions.indexOf(display || validOptions[0]);
    const nextDisplay = validOptions[(currentIdx + 1) % validOptions.length];
    router.get(
      buildUrlWithDisplay(scale, getTimeKey(), view, range, nextDisplay),
      {},
      { preserveScroll: true },
    );
  }

  function getTimeKey(): string {
    switch (scale) {
      case 'year':
        return String(currentYear);
      case 'week':
        return currentWeek;
      default:
        return currentMonth;
    }
  }

  function buildUrlWithDisplay(
    targetScale: Scale,
    timeKey: string,
    targetView?: View,
    targetRange?: Range,
    targetDisplay?: DisplayFilter | null,
  ): string {
    const params = new URLSearchParams();
    const v = targetView ?? view;
    const r = targetRange !== undefined ? targetRange : range;
    const d = targetDisplay !== undefined ? targetDisplay : display;

    const defaultView = viewConfig.defaultViewForScale[targetScale];
    if (v !== defaultView) params.set('view', v);
    const rangeParam = getEffectiveRangeParam(
      r,
      v,
      targetScale,
      viewConfig.defaultRanges,
      viewConfig.viewSpecificDefaultRanges,
    );
    if (rangeParam) params.set('range', rangeParam);
    const defaultDisplay = viewConfig.defaultDisplay[v];
    if (d && defaultDisplay && d !== defaultDisplay) params.set('display', d);

    const query = params.toString();
    const queryStr = query ? `?${query}` : '';
    switch (targetScale) {
      case 'year':
        return `/year/${timeKey}${queryStr}`;
      case 'week':
        return `/week/${timeKey}${queryStr}`;
      default:
        return `/month/${timeKey}${queryStr}`;
    }
  }

  // Keyboard shortcuts
  function handleKeydown(e: KeyboardEvent) {
    if (
      e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLTextAreaElement ||
      (e.target instanceof HTMLElement && e.target.isContentEditable)
    ) {
      return;
    }

    if (shouldHandleTodayHotkey(e, { dialogOpen: isDialogOpen() })) {
      goToToday();
      return;
    }

    switch (e.key) {
      case 'ArrowLeft':
        goToPrevious();
        break;
      case 'ArrowRight':
        goToNext();
        break;
      case '?':
        toggleShortcutsBar();
        break;
      case 's':
      case 'S':
        cycleScale();
        break;
      case 'r':
      case 'R':
        cycleRange();
        break;
      case 'd':
      case 'D':
        cycleDisplay();
        break;
      case 'v':
      case 'V':
        cycleView();
        break;
      case '1':
      case '2':
      case '3':
      case '4':
      case '5':
      case '6':
      case '7':
      case '8':
      case '9':
        jumpToFavorite(parseInt(e.key) - 1);
        break;
    }
  }

  function moveDraggedProjectToBacklog(event: DragEvent) {
    event.preventDefault();
    if (!$draggedProject || $draggedProject.schedulingState === 'backlog') return;
    requestProjectMove($draggedProject.id, $draggedProject, 'backlog', {});
  }

  function jumpToFavorite(index: number) {
    const sorted = [...currentFavorites].sort((a, b) => a.position - b.position);
    const fav = sorted[index];
    if (!fav) return;
    navigateToFavorite(fav, viewConfig);
  }

  function buildUrl(targetScale: Scale, timeKey: string, targetView?: View, targetRange?: Range) {
    const params = new URLSearchParams();
    const v = targetView ?? view;
    const r = targetRange !== undefined ? targetRange : range;

    const defaultView = viewConfig.defaultViewForScale[targetScale];
    if (v !== defaultView) {
      params.set('view', v);
    }
    const rangeParam = getEffectiveRangeParam(
      r,
      v,
      targetScale,
      viewConfig.defaultRanges,
      viewConfig.viewSpecificDefaultRanges,
    );
    if (rangeParam) {
      params.set('range', rangeParam);
    }
    const defaultDisplay = viewConfig.defaultDisplay[v];
    if (display && defaultDisplay && display !== defaultDisplay) {
      params.set('display', display);
    }
    const query = params.toString();
    const queryStr = query ? `?${query}` : '';

    switch (targetScale) {
      case 'year':
        return `/year/${timeKey}${queryStr}`;
      case 'week':
        return `/week/${timeKey}${queryStr}`;
      default:
        return `/month/${timeKey}${queryStr}`;
    }
  }

  function goToPrevious() {
    if (isYearScale) {
      router.get(buildUrl('year', String(currentYear - 1)), {}, { preserveScroll: true });
    } else if (isWeekScale) {
      const prevWeek = getPreviousTimeframe(currentWeek, range || 'month');
      router.get(buildUrl('week', prevWeek), {}, { preserveScroll: true });
    } else {
      // Month scale: focus view navigates by 1 month, others by range
      if (view === 'focus') {
        const prevMonth = getPreviousMonthKey(currentMonth);
        router.get(buildUrl('month', prevMonth), {}, { preserveScroll: true });
      } else {
        const prevMonth = getPreviousMonthTimeframe(currentMonth, range || 'quarter');
        router.get(buildUrl('month', prevMonth), {}, { preserveScroll: true });
      }
    }
  }

  function goToNext() {
    if (isYearScale) {
      router.get(buildUrl('year', String(currentYear + 1)), {}, { preserveScroll: true });
    } else if (isWeekScale) {
      const nextWeek = getNextTimeframe(currentWeek, range || 'month');
      router.get(buildUrl('week', nextWeek), {}, { preserveScroll: true });
    } else {
      // Month scale: focus view navigates by 1 month, others by range
      if (view === 'focus') {
        const nextMonth = getNextMonthKey(currentMonth);
        router.get(buildUrl('month', nextMonth), {}, { preserveScroll: true });
      } else {
        const nextMonth = getNextMonthTimeframe(currentMonth, range || 'quarter');
        router.get(buildUrl('month', nextMonth), {}, { preserveScroll: true });
      }
    }
  }

  /** Navigates to the current period. False when this already is the current period. */
  function goToToday(): boolean {
    if (isYearScale) {
      const thisYear = new Date().getFullYear();
      if (thisYear === currentYear) return false;
      router.get(buildUrl('year', String(thisYear)), {}, { preserveScroll: true });
    } else if (isWeekScale) {
      const todayWeek = getCurrentWeekKey();
      if (todayWeek === currentWeek) return false;
      router.get(buildUrl('week', todayWeek), {}, { preserveScroll: true });
    } else {
      const today = getCurrentMonthKey();
      if (today === currentMonth) return false;
      router.get(buildUrl('month', today), {}, { preserveScroll: true });
    }
    return true;
  }

  // Range helpers for month views (convert to expected type)
  let monthRange = $derived<MonthRange>(
    range === '1-month' ||
      range === 'quarter' ||
      range === '3-months' ||
      range === '6-months' ||
      range === 'year'
      ? (range as MonthRange)
      : 'quarter',
  );

  // Range helpers for week views (convert to expected type)
  let weekRange = $derived<WeekPeriod>(
    range === '1-week' || range === 'month' || range === '5' || range === '7' || range === 'quarter'
      ? (range as WeekPeriod)
      : 'month',
  );

  // Range helpers for year table view
  let tableRange = $derived<TablePeriod>(
    range === 'full' || range === 'half' || range === 'quarter' ? (range as TablePeriod) : 'full',
  );

  // Merge all months' projects into a single key for year-focus view
  // This lets MonthFocusView render all year's projects in its kanban layout
  let yearMergedProjectsByMonth = $derived.by(
    (): Record<string, import('../lib/types').ProjectsByStatus> => {
      if (!isYearScale) return projectsByMonth;
      const merged: import('../lib/types').ProjectsByStatus = {
        planned: [],
        active: [],
        almost: [],
        done: [],
      };
      const seen = new Set<string>();
      for (const monthData of Object.values(projectsByMonth)) {
        for (const [status, projects] of Object.entries(monthData)) {
          const key = status as import('../lib/types').ProjectStatus;
          for (const p of projects as import('../lib/types').Project[]) {
            if (!seen.has(p.id)) {
              seen.add(p.id);
              merged[key].push(p);
            }
          }
        }
      }
      return { [currentMonth]: merged };
    },
  );
</script>

<svelte:window onkeydown={handleKeydown} />

<div class="app-container app-min-width">
  <!-- Header: mobile top bar + view switcher vs desktop single-row -->
  {#if $viewport.isMobile}
    <TimelineTopBar
      {currentMonth}
      {currentWeek}
      {currentYear}
      {scale}
      {view}
      {range}
      {display}
      {viewConfig}
      onPrevious={goToPrevious}
      onNext={goToNext}
      onToday={goToTodayFromHeader}
      showPeriodArrows={!showMobileDays}
    />
  {:else}
    <Header
      {currentMonth}
      {currentWeek}
      {currentYear}
      {scale}
      {view}
      {range}
      {display}
      {viewConfig}
      onPrevious={goToPrevious}
      onNext={goToNext}
      onToday={goToToday}
    />
  {/if}

  <div class="app-body">
    <!-- Main content area -->
    <main
      class="main-content custom-scrollbar"
      class:full-width={!showDesktopOnlyNotice &&
        (viewConfig.fullWidthViews?.includes(view) ?? false)}
    >
      {#if showDesktopOnlyNotice}
        <DesktopOnlyNotice
          feature="The {viewConfig.viewLabels[view]?.label ?? view} view"
          actions={mobileViewActions}
        />
      {:else if isYearScale}
        <!-- Year views -->
        {#if viewComponent === 'year-focus'}
          <MonthFocusView
            visibleMonths={[currentMonth]}
            projectsByMonth={yearMergedProjectsByMonth}
            events={[]}
            {currentMonth}
            {display}
            headerLabel={String(currentYear)}
            hideWeekPills
          />
        {:else if viewComponent === 'year-tasks'}
          <TaskKanbanView {projectsByMonth} {currentMonth} />
        {:else if viewComponent === 'year-grid-6x2'}
          <YearGrid6x2View {visibleMonths} {projectsByMonth} {events} {currentMonth} {display} />
        {:else if viewComponent === 'year-grid-quarters'}
          <YearGridQuartersView
            {visibleMonths}
            {projectsByMonth}
            {events}
            {currentMonth}
            {display}
          />
        {:else if viewComponent === 'year-table'}
          <YearTableView
            {visibleMonths}
            {projectsByMonth}
            {events}
            {currentMonth}
            period={tableRange}
            {display}
          />
        {/if}
      {:else if isWeekScale}
        <!-- Week views -->
        {#if viewComponent === 'week-table'}
          <WeekTableView
            {visibleWeeks}
            {projectsByWeek}
            {events}
            {currentWeek}
            period={weekRange}
            {display}
          />
        {:else if viewComponent === 'week-table-days' && showMobileDays}
          {#key `${currentWeek}|${range}`}
            <MobileDaysAgenda
              bind:this={mobileDaysAgenda}
              {visibleWeeks}
              {events}
              {currentWeek}
              allProjects={scheduledProjects}
              onPrevious={goToPrevious}
              onNext={goToNext}
              onShowAllProjects={() => goToMobileView('focus')}
            />
          {/key}
        {:else if viewComponent === 'week-table-days'}
          <WeekTableDaysView
            {visibleWeeks}
            {events}
            {currentWeek}
            period={weekRange}
            daysScale="week"
            allProjects={scheduledProjects}
          />
        {:else if viewComponent === 'week-focus'}
          <WeekFocusView
            {visibleWeeks}
            {projectsByWeek}
            {events}
            {currentWeek}
            period={weekRange}
            {display}
          />
        {:else if viewComponent === 'week-rolling'}
          <WeekRollingView
            {visibleWeeks}
            {projectsByWeek}
            {events}
            {currentWeek}
            period={weekRange}
            {display}
          />
        {:else if viewComponent === 'week-timeline'}
          <WeekTimelineView
            {visibleWeeks}
            {projectsByWeek}
            {events}
            {currentWeek}
            period={weekRange}
            {display}
          />
        {:else if viewComponent === 'week-gantt'}
          <GanttChartView
            scale="week"
            {visibleWeeks}
            {projectsByWeek}
            {currentWeek}
            range={range || 'quarter'}
          />
        {:else if viewComponent === 'week-tasks'}
          <TaskKanbanView {projectsByWeek} {currentWeek} />
        {/if}
      {:else}
        <!-- Month views -->
        {#if viewComponent === 'month-focus'}
          <!-- Month focus: pure kanban for current month -->
          <MonthFocusView {visibleMonths} {projectsByMonth} {events} {currentMonth} {display} />
        {:else if viewComponent === 'month-timeline'}
          <MonthTimelineView
            {visibleMonths}
            {projectsByMonth}
            {events}
            {currentMonth}
            range={monthRange}
            {display}
          />
        {:else if viewComponent === 'month-table-days' && showMobileDays}
          {#key `${currentMonth}|${range}`}
            <MobileDaysAgenda
              bind:this={mobileDaysAgenda}
              {visibleWeeks}
              {events}
              {currentWeek}
              allProjects={scheduledProjects}
              onPrevious={goToPrevious}
              onNext={goToNext}
              onShowAllProjects={() => goToMobileView('focus')}
            />
          {/key}
        {:else if viewComponent === 'month-table-days'}
          <WeekTableDaysView
            {visibleWeeks}
            {events}
            {currentWeek}
            {currentMonth}
            period={weekRange}
            daysScale="month"
            allProjects={scheduledProjects}
          />
        {:else if viewComponent === 'month-rolling'}
          <MonthRollingView
            {visibleMonths}
            {projectsByMonth}
            {events}
            {currentMonth}
            range={monthRange}
            {display}
          />
        {:else if viewComponent === 'month-gantt'}
          <GanttChartView
            scale="month"
            {visibleMonths}
            {projectsByMonth}
            {currentMonth}
            range={range || 'year'}
          />
        {:else if viewComponent === 'month-tasks'}
          <TaskKanbanView {projectsByMonth} {currentMonth} />
        {/if}
      {/if}
    </main>

    <!-- Right rails: mobile bottom sheets vs desktop inline -->
    {#if $viewport.isMobile}
      <MobileSheet
        open={isRailSheetOpen}
        direction="bottom"
        label={railSheetLabel}
        onClose={closeAllRightRails}
      >
        <StatusRail {projectsByMonth} />
        <MonthRail {projectsByMonth} {projectsByWeek} />
        <EventsRail {events} />
        <BacklogRail projects={backlogProjects} />
        <PriorityTasksRail allProjects={scheduledProjects} backlogCount={backlogProjects.length} />
      </MobileSheet>
      <ProjectRail allProjects={allProjectsFlat} />
    {:else}
      <StatusRail {projectsByMonth} />
      <MonthRail {projectsByMonth} {projectsByWeek} />
      <EventsRail {events} />
      <ProjectRail allProjects={allProjectsFlat} />
      <BacklogRail projects={backlogProjects} />
      <PriorityTasksRail allProjects={scheduledProjects} backlogCount={backlogProjects.length} />
    {/if}
  </div>

  {#if $draggedProject && $draggedProject.schedulingState !== 'backlog'}
    <div
      class="backlog-drop-target"
      role="status"
      ondragover={(event) => event.preventDefault()}
      ondrop={moveDraggedProjectToBacklog}
    >
      Drop to move <strong>{$draggedProject.title}</strong> to backlog
    </div>
  {/if}

  <!-- Keyboard shortcuts help (desktop only) -->
  {#if !$viewport.isMobile}
    {#if $isShortcutsMinimized}
      <button
        class="shortcuts-pill kbd-hint"
        onclick={toggleShortcutsBar}
        title="Show shortcuts (?)"
      >
        <span>?</span>
      </button>
    {:else}
      <div class="shortcuts-help kbd-hint">
        <span class="shortcut-key">B P E C Y H</span> Boards • Projects • Timeline • Calendar •
        Cycles • Habits
        <span class="sep">•</span>
        <span class="shortcut-key">← →</span> Navigate
        <span class="sep">•</span>
        <span class="shortcut-key">T or 0</span> Today
        <span class="sep">•</span>
        <span class="shortcut-key">S</span> Scale
        <span class="sep">•</span>
        <span class="shortcut-key">R</span> Range
        <span class="sep">•</span>
        <span class="shortcut-key">D</span> Display
        <span class="sep">•</span>
        <span class="shortcut-key">V</span> View
        <span class="sep">•</span>
        <span class="shortcut-key">K or /</span> Search
        <span class="sep">•</span>
        <span class="shortcut-key">W</span> Work Queue
        <span class="sep">•</span>
        <span class="shortcut-key">N</span> Now
        {#if currentFavorites.length > 0}
          <span class="sep">•</span>
          <span class="shortcut-key">1-{Math.min(currentFavorites.length, 9)}</span> Favorites
        {/if}
        <button class="shortcuts-close" onclick={toggleShortcutsBar} title="Minimize (?)">×</button>
      </div>
    {/if}
  {/if}

  <!-- Modals -->
  <EventModal monthKey={currentMonth} />
  <ProjectModal />
  <MoveConfirmModal />
  <EventMoveConfirmModal />

  <!-- Day cell popover (for Days view hover) -->
  <DayCellPopover />
  <ProjectTooltip />
  <ProjectCellPopover />
  <HoveredDateLabel />
</div>

<style>
  .backlog-drop-target {
    position: fixed;
    right: 1rem;
    bottom: 1rem;
    z-index: 40;
    border: 2px dashed #60a5fa;
    border-radius: 0.5rem;
    background: #eff6ff;
    color: #1d4ed8;
    font-size: 0.75rem;
    padding: 0.625rem 0.875rem;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.15);
  }

  .app-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: #f9fafb;
  }

  .app-body {
    display: flex;
    flex: 1;
    overflow: hidden;
  }

  .main-content {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
  }

  /* On mobile, reduce padding and allow content to take full width */
  @media (max-width: 767px) {
    .main-content {
      padding: 0.5rem;
      overflow-x: auto;
    }
  }

  .main-content.full-width {
    padding: 0;
    overflow: hidden;
  }

  .shortcuts-help {
    position: fixed;
    bottom: 0.5rem;
    left: 50%;
    transform: translateX(-50%);
    font-size: 0.575rem;
    color: #9ca3af;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(4px);
    padding: 0.25rem 0.5rem;
    border-radius: 0.375rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    border: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    white-space: nowrap;
    z-index: 50;
    line-height: 1.2;
  }

  .shortcut-key {
    font-weight: 600;
    color: #6b7280;
    background: #f3f4f6;
    padding: 0 0.2rem;
    border-radius: 0.15rem;
    font-size: 0.55rem;
  }

  .sep {
    color: #d1d5db;
    margin: 0 0.05rem;
  }

  .shortcuts-close {
    margin-left: 0.35rem;
    background: none;
    border: none;
    color: #9ca3af;
    cursor: pointer;
    font-size: 0.7rem;
    line-height: 1;
    padding: 0 0.15rem;
    border-radius: 0.15rem;
  }

  .shortcuts-close:hover {
    color: #6b7280;
    background: #f3f4f6;
  }

  .shortcuts-pill {
    position: fixed;
    bottom: 0.5rem;
    left: 50%;
    transform: translateX(-50%);
    font-size: 0.6rem;
    font-weight: 600;
    color: #9ca3af;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(4px);
    padding: 0.2rem 0.45rem;
    border-radius: 0.375rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    border: 1px solid #e5e7eb;
    cursor: pointer;
    z-index: 50;
    line-height: 1;
  }

  .shortcuts-pill:hover {
    color: #6b7280;
    background: rgba(255, 255, 255, 1);
  }
</style>
